import { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import { SearchInput } from "src/common/components/SearchInput";
import {
  setMediaSearch,
  resetMediaSearch,
  fetchFilteredMedias,
  getMediaSearch,
} from "../state/gallerySlice";
import { MEDIA_LIST_COMPONENT_ID } from "../Gallery";

const MediaSearchBar = ({
  className,
  allowedMediaTypeOverride,
  componentId,
}: {
  className?: string;
  allowedMediaTypeOverride?: string;
  componentId?: string;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const mediaSearch = useAppSelector(getMediaSearch);
  const { limit, sort } = mediaSearch;

  const [input, setInput] = useState(mediaSearch.query || "");
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state with Redux state (e.g., when navigating or when query is reset externally)
  useEffect(() => {
    setInput(mediaSearch.query || "");
  }, [mediaSearch.query]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setInput(value);

    // Cancel previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // When clearing, dispatch immediately without debounce
    if (!value.trim()) {
      if (!allowedMediaTypeOverride) {
        dispatch(resetMediaSearch());
      }
      void dispatch(
        fetchFilteredMedias({
          page: 0,
          limit,
          sort,
          mediaType: allowedMediaTypeOverride || "all",
          owner: "all",
          media360tag: "",
          query: "",
          componentId: componentId || MEDIA_LIST_COMPONENT_ID,
        }),
      );
      return;
    }

    // Debounce the server request (200ms)
    debounceRef.current = setTimeout(() => {
      const searchParams = {
        page: 0,
        mediaType: allowedMediaTypeOverride || mediaSearch.mediaType || "all",
        owner: mediaSearch.owner || "all",
        media360tag: mediaSearch.media360tag || "",
        query: value.trim(),
      };

      dispatch(setMediaSearch(searchParams));
      void dispatch(
        fetchFilteredMedias({
          ...searchParams,
          limit,
          sort,
          componentId: componentId || MEDIA_LIST_COMPONENT_ID,
        }),
      );
    }, 200);
  };

  return (
    <div className={`search-filter flex items-end w-[15rem] ${className}`}>
      <SearchInput
        value={input}
        onChange={handleSearchChange}
        placeholder={`${t("general.search")}...`}
        className="w-full"
      />
    </div>
  );
};

export default MediaSearchBar;
